/**
 * /university/tuners — list of registered tuners.
 */
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronLeft, Loader2 } from 'lucide-react';

import { TunerCard } from '../../../../components/university/tuner-card';
import { learningApi, learningKeys } from '../../../../lib/api/learning';

function TunersIndexRoute() {
  const tunersQ = useQuery({
    queryKey: learningKeys.tunersList(),
    queryFn: () => learningApi.listTuners(),
    staleTime: 30_000,
  });

  const tuners = tunersQ.data?.tuners ?? [];

  return (
    <section className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-6">
      <header className="flex flex-col gap-1">
        <Link
          to="/university"
          className="inline-flex w-fit items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" aria-hidden /> Back to University
        </Link>
        <div className="flex items-center justify-between">
          <h1 className="text-[16px] font-semibold tracking-tight text-foreground">
            Tuners
            <span className="ml-2 text-[12px] font-normal text-muted-foreground">
              {tuners.length} {tuners.length === 1 ? 'tuner' : 'tuners'}
            </span>
          </h1>
          {tunersQ.isFetching ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> syncing…
            </span>
          ) : null}
        </div>
      </header>

      {tunersQ.isPending ? (
        <div className="px-2 py-4 text-[12px] text-muted-foreground">Loading tuners…</div>
      ) : tunersQ.isError ? (
        <div className="rounded border border-destructive/50 bg-destructive/5 p-3 text-[12px] text-destructive">
          Failed to load tuners: {(tunersQ.error as Error).message}
        </div>
      ) : tuners.length === 0 ? (
        <div className="px-2 py-6 text-center text-[12px] text-muted-foreground/70">
          No tuners registered yet.
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {tuners.map((tuner) => (
            <li key={tuner.tunerId}>
              <TunerCard tuner={tuner} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export const Route = createFileRoute('/_authenticated/university/tuners/')({
  component: TunersIndexRoute,
});
